// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";

// Utils
import { num } from "../../shared/formatters";

type RangeType = {
  breakPrismDioptres: number | null;
  recoveryPrismDioptres: number | null;
  isLowerBound: boolean;
} | null;

type FusionalRangeChartType = {
  convergence: RangeType;
  divergence: RangeType;
};

// Wide and short rather than square: at 100% container width, an SVG's rendered height follows
// this viewBox's own aspect ratio, so a wide/flat ratio keeps the chart compact at any width.
const W = 600;
const H = 74;
const L = 16;
const R = 16;
const BAR_Y = 34;
const BAR_H = 12;
const AXIS_Y = 40;

const RANGE_COLOR = "#E2772E";
const RECOVERY_COLOR = "#202338";

// A shared zero: divergence (base in) runs left, convergence (base out) runs right, both scaled
// against whichever side's break is larger so both ranges stay readable on one axis.
const FusionalRangeChart = ({
  convergence,
  divergence,
}: FusionalRangeChartType) => {
  const { t } = useTranslation();

  const geometry = useMemo(() => {
    const track = W - L - R;
    const cx = L + track / 2;
    const halfTrack = track / 2 - 4;
    const magnitudes = [
      convergence?.breakPrismDioptres,
      divergence?.breakPrismDioptres,
    ].filter((v): v is number => v !== null && v !== undefined);
    const max = Math.max(...magnitudes, 1) * 1.15;
    const sx = (value: number) => (value / max) * halfTrack;
    return { cx, sx };
  }, [convergence?.breakPrismDioptres, divergence?.breakPrismDioptres]);

  if (!convergence && !divergence) return null;

  const { cx, sx } = geometry;
  const hasDivergenceBreak =
    divergence?.breakPrismDioptres !== null &&
    divergence?.breakPrismDioptres !== undefined;
  const hasDivergenceRecovery =
    divergence?.recoveryPrismDioptres !== null &&
    divergence?.recoveryPrismDioptres !== undefined;
  const hasConvergenceBreak =
    convergence?.breakPrismDioptres !== null &&
    convergence?.breakPrismDioptres !== undefined;
  const hasConvergenceRecovery =
    convergence?.recoveryPrismDioptres !== null &&
    convergence?.recoveryPrismDioptres !== undefined;

  return (
    <Box sx={{ borderBottom: "1px solid #5B6D7A40", mb: "8px" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={t("Fusional ranges")}
        style={{ width: "100%", height: "auto" }}
      >
        <line x1={L} y1={AXIS_Y} x2={W - R} y2={AXIS_Y} stroke="#5B6D7A80" />
        <line x1={cx} y1={24} x2={cx} y2={56} stroke="#5B6D7A80" />
        <text x={cx} y={68} fontSize="10" fill="#5B6D7A" textAnchor="middle">
          0
        </text>
        <text x={L} y={10} fontSize="10" fill="#5B6D7A">
          {t("Divergence (base in)")}
        </text>
        <text x={W - R} y={10} fontSize="10" fill="#5B6D7A" textAnchor="end">
          {t("Convergence (base out)")}
        </text>

        {hasDivergenceBreak && (
          <>
            <rect
              x={cx - sx(divergence.breakPrismDioptres as number)}
              y={BAR_Y}
              width={sx(divergence.breakPrismDioptres as number)}
              height={BAR_H}
              rx={2}
              fill={RANGE_COLOR}
            />
            <text
              x={cx - sx(divergence.breakPrismDioptres as number)}
              y={BAR_Y - 6}
              fontSize="11"
              fontWeight={600}
              fill="#202338"
              textAnchor="start"
            >
              {divergence.isLowerBound ? "▶ " : ""}
              {num(divergence.breakPrismDioptres, 1, "Δ")}
            </text>
            {hasDivergenceRecovery && (
              <>
                <line
                  x1={cx - sx(divergence.recoveryPrismDioptres as number)}
                  y1={30}
                  x2={cx - sx(divergence.recoveryPrismDioptres as number)}
                  y2={50}
                  stroke={RECOVERY_COLOR}
                  strokeWidth={2}
                />
                <text
                  x={cx - sx(divergence.recoveryPrismDioptres as number)}
                  y={62}
                  fontSize="10"
                  fill="#5B6D7A"
                  textAnchor="middle"
                >
                  {num(divergence.recoveryPrismDioptres)}
                </text>
              </>
            )}
          </>
        )}

        {hasConvergenceBreak && (
          <>
            <rect
              x={cx}
              y={BAR_Y}
              width={sx(convergence.breakPrismDioptres as number)}
              height={BAR_H}
              rx={2}
              fill={RANGE_COLOR}
            />
            <text
              x={cx + sx(convergence.breakPrismDioptres as number)}
              y={BAR_Y - 6}
              fontSize="11"
              fontWeight={600}
              fill="#202338"
              textAnchor="end"
            >
              {convergence.isLowerBound ? "▶ " : ""}
              {num(convergence.breakPrismDioptres, 1, "Δ")}
            </text>
            {hasConvergenceRecovery && (
              <>
                <line
                  x1={cx + sx(convergence.recoveryPrismDioptres as number)}
                  y1={30}
                  x2={cx + sx(convergence.recoveryPrismDioptres as number)}
                  y2={50}
                  stroke={RECOVERY_COLOR}
                  strokeWidth={2}
                />
                <text
                  x={cx + sx(convergence.recoveryPrismDioptres as number)}
                  y={62}
                  fontSize="10"
                  fill="#5B6D7A"
                  textAnchor="middle"
                >
                  {num(convergence.recoveryPrismDioptres)}
                </text>
              </>
            )}
          </>
        )}
      </svg>

      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        columnGap="8px"
        sx={{ mb: "8px", fontSize: "12px", color: "#5B6D7A" }}
      >
        <Box
          component="span"
          sx={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: RANGE_COLOR,
          }}
        />
        {t("to the break")}
        <Box
          component="span"
          sx={{
            width: "2px",
            height: "10px",
            backgroundColor: RECOVERY_COLOR,
          }}
        />
        {t("recovery")}
        <Box component="span">▶</Box>
        {t("ramp ran out first (lower bound)")}
      </Box>
    </Box>
  );
};

export default FusionalRangeChart;
